import {
  ProductCreateRequest,
  ProductDuplicateRequest,
  ProductResponse,
  ProductStatus,
  ProductUpdateRequest,
} from "../../../types/product.type";
import { roundHalfUp } from "../../../utils/money";
import { VAT_RATE_PERCENT } from "../../../utils/vat";
import { isValidUnitPrice } from "../PriceBook/Details/lineEdits";

/** The face price is a pricebook unit price, so the API takes it at 2 decimals at most. */
export const FACE_PRICE_FRACTION_DIGITS = 2;
/** Column widths of `product.dimension_text` and `product.fda_no`. */
export const DIMENSION_TEXT_MAX_LENGTH = 50;
export const FDA_NO_MAX_LENGTH = 30;
/** `product.sequence` is a Postgres INTEGER, so a larger ลำดับสินค้า cannot be stored. */
export const PRODUCT_SEQUENCE_MAX = 2147483647;

const WHOLE_NUMBER_PATTERN = /^\d+$/;

export interface ProductForm {
  code: string;
  name: string;
  categoryCode: string;
  sequence: string;
  description: string;
  brand: string;
  barcode1: string;
  barcode2: string;
  reference1: string;
  dimensionText: string;
  fdaNo: string;
  shelfLifeDays: string;
  weightGram: string;
  costStd: string;
  facePrice: string;
  status: ProductStatus;
  allowExport: boolean;
  excludeFromWaste: boolean;
  thumbnailUrl: string | null;
  pictureUrl: string | null;
}

export type ProductFormErrors = Partial<Record<keyof ProductForm, string>>;

export const INITIAL_PRODUCT_FORM: ProductForm = {
  code: "",
  name: "",
  categoryCode: "",
  sequence: "",
  description: "",
  brand: "",
  barcode1: "",
  barcode2: "",
  reference1: "",
  dimensionText: "",
  fdaNo: "",
  shelfLifeDays: "",
  weightGram: "",
  costStd: "",
  facePrice: "",
  status: "enable",
  // The API exports a product unless told otherwise, so a new one starts switched on.
  allowExport: true,
  excludeFromWaste: false,
  thumbnailUrl: null,
  pictureUrl: null,
};

const toText = (value: string | null) => value ?? "";

const toAmountText = (value?: number | null) =>
  value === null || value === undefined ? "" : String(value);

const toNumberOrNull = (value: string) =>
  value.trim() === "" ? null : Number(value);

const toTrimmedOrNull = (value: string) => value.trim() || null;

/**
 * A legacy face price can carry 4 decimals; the form shows it at the 2 the API now takes,
 * which is also the face price the pricebook lines are compared against.
 */
const toFacePriceText = (facePrice: number | null) =>
  facePrice === null
    ? ""
    : String(roundHalfUp(facePrice, FACE_PRICE_FRACTION_DIGITS));

/** Whether the typed face price differs from the stored one, read at 2 decimals. */
export const isFacePriceChanged = (
  facePriceText: string,
  storedFacePrice: number | null,
): boolean =>
  storedFacePrice === null ||
  Number(facePriceText) !==
    roundHalfUp(storedFacePrice, FACE_PRICE_FRACTION_DIGITS);

/**
 * `null` clears the stored image path and `undefined` leaves it untouched. A
 * picked file is written by the images endpoint, so its path stays untouched here.
 */
const clearedImagePath = (file: File | null, url: string | null) =>
  !file && url === null ? null : undefined;

export const mapProductToForm = (product: ProductResponse): ProductForm => ({
  code: product.code,
  name: product.name,
  categoryCode: toText(product.category_code),
  sequence: toAmountText(product.sequence),
  description: toText(product.description),
  brand: toText(product.brand),
  barcode1: toText(product.barcode1),
  barcode2: toText(product.barcode2),
  reference1: toText(product.reference1),
  dimensionText: toText(product.dimension_text),
  fdaNo: toText(product.fda_no),
  shelfLifeDays: toAmountText(product.shelf_life_days),
  weightGram: toAmountText(product.weight_gram),
  costStd: toAmountText(product.cost_std),
  facePrice: toFacePriceText(product.face_price),
  status: product.status,
  allowExport: product.allow_export,
  excludeFromWaste: product.exclude_from_waste,
  thumbnailUrl: product.thumbnail,
  pictureUrl: product.picture,
});

/** ลำดับสินค้า is optional; when typed it must be a whole number the column can hold. */
const validateSequence = (sequenceText: string): string | undefined => {
  const sequence = sequenceText.trim();
  if (!sequence) return undefined;
  if (!WHOLE_NUMBER_PATTERN.test(sequence))
    return "ลำดับสินค้าต้องเป็นจำนวนเต็มตั้งแต่ 0 ขึ้นไป";
  if (Number(sequence) > PRODUCT_SEQUENCE_MAX)
    return "ลำดับสินค้าสูงเกินที่ระบบรับได้";
  return undefined;
};

/** The form's problems by field; empty when it may be saved. The code is checked only while it can be typed. */
export const validateProductForm = (
  form: ProductForm,
  isCodeEditable: boolean,
): ProductFormErrors => {
  const errors: ProductFormErrors = {};

  if (isCodeEditable && !form.code.trim()) errors.code = "กรุณากรอกรหัสสินค้า";
  if (!form.name.trim()) errors.name = "กรุณากรอกชื่อรายการ";
  if (!form.categoryCode) errors.categoryCode = "กรุณาเลือกหมวดสินค้า";
  const sequenceError = validateSequence(form.sequence);
  if (sequenceError) errors.sequence = sequenceError;
  if (!form.facePrice.trim()) errors.facePrice = "กรุณากรอกราคาหน้าซอง";
  else if (!isValidUnitPrice(form.facePrice))
    errors.facePrice = "ราคาหน้าซองไม่ถูกต้อง หรือสูงเกินที่ระบบรับได้";

  return errors;
};

/**
 * The fields every write shares. A create and a duplicate always send `face_price`, which
 * the form requires, so a saved product can never fall back to being priced at zero.
 */
export const buildProductPayload = (
  form: ProductForm,
  thumbnailFile: File | null,
  pictureFile: File | null,
): ProductDuplicateRequest => ({
  name: form.name.trim(),
  category_code: form.categoryCode,
  sequence: toNumberOrNull(form.sequence),
  description: toTrimmedOrNull(form.description),
  brand: toTrimmedOrNull(form.brand),
  barcode1: toTrimmedOrNull(form.barcode1),
  barcode2: toTrimmedOrNull(form.barcode2),
  reference1: toTrimmedOrNull(form.reference1),
  dimension_text: toTrimmedOrNull(form.dimensionText),
  fda_no: toTrimmedOrNull(form.fdaNo),
  shelf_life_days: toNumberOrNull(form.shelfLifeDays),
  weight_gram: toNumberOrNull(form.weightGram),
  cost_std: toNumberOrNull(form.costStd),
  face_price: Number(form.facePrice),
  tax_rate: VAT_RATE_PERCENT,
  status: form.status,
  allow_export: form.allowExport,
  exclude_from_waste: form.excludeFromWaste,
  thumbnail: clearedImagePath(thumbnailFile, form.thumbnailUrl),
  picture: clearedImagePath(pictureFile, form.pictureUrl),
});

/**
 * The edit surface. The face price goes only when it changed — a legacy 4-decimal face price
 * resent untouched would be refused — and with it the pricebooks the user chose to move along.
 */
export const buildProductUpdatePayload = (
  form: ProductForm,
  thumbnailFile: File | null,
  pictureFile: File | null,
  storedFacePrice: number | null,
  applyFacePriceTo: string[],
): ProductUpdateRequest => {
  const { face_price: facePrice, ...payload } = buildProductPayload(
    form,
    thumbnailFile,
    pictureFile,
  );
  if (!isFacePriceChanged(form.facePrice, storedFacePrice)) return payload;
  return {
    ...payload,
    face_price: facePrice,
    apply_face_price_to: applyFacePriceTo,
  };
};

/**
 * The create surface. `code` is POST-only, and `name` / `face_price` are restated
 * because POST requires them while the shared payload types them as optional.
 */
export const buildProductCreatePayload = (
  form: ProductForm,
  thumbnailFile: File | null,
  pictureFile: File | null,
): ProductCreateRequest => ({
  ...buildProductPayload(form, thumbnailFile, pictureFile),
  code: form.code.trim(),
  name: form.name.trim(),
  face_price: Number(form.facePrice),
});
